import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Form } from "react-bootstrap";
import { Pagination, PaginationProps } from "antd";
import "../../styles/missedscreenshots.scss";

import { commonGetService } from "../../utils/properties";

type SystemStatus = "Online" | "Offline" | "Yet to Track";

interface MissedScreenshotItem {
  employee_id: string;
  employee_name: string;
  employee_mail: string;
  employee_image?: string;
  department: string;
  designation: string;
  system_status: SystemStatus | string;
  emp_status?: string;
  last_screenshot_capture?: string;
  last_screenshot_capture_ago: string;
}

interface FilterOption {
  filterValue: string;
  isSelect: boolean;
  value?: string;
}

type FilterId = "department" | "designation" | "systemStatus";

type FilterState = Record<FilterId, FilterOption[]>;

const STATIC_STATUS_OPTIONS: FilterOption[] = [
  { filterValue: "Online", isSelect: false, value: "Online" },
  { filterValue: "Offline", isSelect: false, value: "Offline" },
  { filterValue: "Yet to Track", isSelect: false, value: "Yet to Track" },
];

// Config-driven filter fields — desktop InlineFilter row and the tablet
// modal both iterate this instead of hand-rolling three near-identical
// blocks each.
const FILTER_FIELDS: { id: FilterId; label: string; placeholder: string }[] = [
  { id: "department", label: "Department", placeholder: "Select Department" },
  { id: "designation", label: "Designation", placeholder: "Select Designation" },
  { id: "systemStatus", label: "System Status", placeholder: "Select Status" },
];

const DEPARTMENTS_ENDPOINT = "/api/1.0/role/departments";
const DESIGNATIONS_ENDPOINT = "/api/1.0/employee/designations";
const SCREENSHOTS_ENDPOINT = "/api/1.0/screenshot/missed-screenshot";
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const EXPORT_HEADERS = [
  "Emp ID",
  "Employee",
  "Department",
  "Designation",
  "Last Screenshot Capture",
  "System Status",
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const getStatusMeta = (status?: string) => {
  const key = (status || "").toLowerCase();

  if (key === "online") return { label: "Online", className: "status-dot status-online" };
  if (key === "offline") return { label: "Offline", className: "status-dot status-offline" };

  return { label: status || "Yet to Track", className: "status-dot status-pending" };
};

/** Parses "31-Aug-2026, 08:28 PM" into a relative string like "10 min ago". */
const getAgoDisplay = (value?: string): string => {
  if (!value) return "";

  const match = value.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4}),?\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return "";

  const [, day, monStr, year, hourStr, minStr, ampm] = match;
  const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === monStr.toLowerCase());
  if (monthIndex === -1) return "";

  let hour = parseInt(hourStr, 10) % 12;
  if (ampm.toUpperCase() === "PM") hour += 12;

  const date = new Date(parseInt(year, 10), monthIndex, parseInt(day, 10), hour, parseInt(minStr, 10));
  const diffMs = Date.now() - date.getTime();
  if (isNaN(diffMs)) return "";

  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHrs = Math.floor(diffMins / 60);
  const remMins = diffMins % 60;
  if (diffHrs < 24) return remMins ? `${diffHrs} hr ${remMins}m ago` : `${diffHrs} hr ago`;

  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

function exportToCsv(rows: MissedScreenshotItem[], filename: string) {
  if (!rows.length) return;

  const escapeCell = (cell: string) => `"${cell.replace(/"/g, '""')}"`;

  const csvRows = rows.map((item) =>
    [
      item.employee_id,
      item.employee_name,
      item.department,
      item.designation,
      item.last_screenshot_capture || "-",
      item.last_screenshot_capture_ago,
      getStatusMeta(item.system_status).label,
    ]
      .map((cell) => escapeCell(String(cell)))
      .join(",")
  );

  const csvContent = [EXPORT_HEADERS.map(escapeCell).join(","), ...csvRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
}

const CheckIcon = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
    <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function CheckboxRow({
  option,
  checked,
  onToggle,
}: {
  option: FilterOption;
  checked: boolean;
  onToggle: (filterValue: string) => void;
}) {
  return (
    <label className={`dropdown-checkbox-row${checked ? " checked" : ""}`}>
      <input type="checkbox" checked={checked} onChange={() => onToggle(option.filterValue)} hidden />
      <span className={`custom-checkbox${checked ? " checked" : ""}`}>{checked && <CheckIcon />}</span>
      <span className="dropdown-checkbox-label">{option.filterValue}</span>
    </label>
  );
}

/** Desktop / normal inline filter. */
const InlineFilter = memo(function InlineFilter({
  id,
  label,
  placeholder,
  options,
  onApply,
  isOpen,
  onToggle,
}: {
  id: FilterId;
  label: string;
  placeholder: string;
  options: FilterOption[];
  onApply: (id: FilterId, options: FilterOption[]) => void;
  isOpen: boolean;
  onToggle: (id: FilterId) => void;
}) {
  const [temp, setTemp] = useState<FilterOption[]>(options);
  const selectedCount = options.filter((o) => o.isSelect).length;

  useEffect(() => {
    if (isOpen) setTemp(options.map((o) => ({ ...o })));
  }, [isOpen, options]);

  const toggleOption = (filterValue: string) => {
    setTemp((prev) =>
      prev.map((o) => (o.filterValue === filterValue ? { ...o, isSelect: !o.isSelect } : o))
    );
  };

  return (
    <div className="inline-filter">
      <button
        type="button"
        className={`inline-filter-trigger${selectedCount ? " active" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => onToggle(id)}
      >
        <span className="inline-filter-label">{label} :</span>
        <span className="inline-filter-value">
          {selectedCount ? `${selectedCount} selected` : placeholder}
          <Image src="/images/dashboard/downarrow.svg" alt="" aria-hidden="true" />
        </span>
      </button>

      {isOpen && (
        <div className="inline-filter-panel" role="listbox">
          {options.length === 0 ? (
            <p className="no-options-text">No options available</p>
          ) : (
            options.map((o) => {
              const checked = !!temp.find((t) => t.filterValue === o.filterValue)?.isSelect;
              return <CheckboxRow key={o.filterValue} option={o} checked={checked} onToggle={toggleOption} />;
            })
          )}

          <div className="inline-filter-actions">
            <button
              type="button"
              className="inline-filter-clear"
              onClick={() => setTemp((prev) => prev.map((o) => ({ ...o, isSelect: false })))}
            >
              Clear
            </button>
            <button type="button" className="inline-filter-apply" onClick={() => onApply(id, temp)}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

/** Tablet modal filter field. */
function ModalFilterField({
  label,
  placeholder,
  options,
  isOpen,
  onToggle,
  onToggleOption,
  onRemoveChip,
}: {
  label: string;
  placeholder: string;
  options: FilterOption[];
  isOpen: boolean;
  onToggle: () => void;
  onToggleOption: (filterValue: string) => void;
  onRemoveChip: (filterValue: string) => void;
}) {
  const selected = options.filter((o) => o.isSelect);
  const visibleChips = selected.slice(0, 2);
  const extraCount = selected.length - visibleChips.length;

  return (
    <div className="modal-filter-field">
      <label className="modal-filter-label">{label}</label>

      <div className={`modal-filter-select-wrapper${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="modal-filter-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          {selected.length === 0 ? (
            <span className="modal-filter-placeholder">{placeholder}</span>
          ) : (
            <span className="modal-filter-chips">
              {visibleChips.map((o) => (
                <span key={o.filterValue} className="modal-filter-chip">
                  {o.filterValue}
                  <span
                    className="modal-filter-chip-remove"
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveChip(o.filterValue);
                    }}
                  >
                    ×
                  </span>
                </span>
              ))}
              {extraCount > 0 && <span className="modal-filter-chip-more">+{extraCount}</span>}
            </span>
          )}

          <svg className="modal-filter-arrow" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path d="M1 1L5 5L9 1" stroke="#6D6D6D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {isOpen && (
          <div className="modal-filter-panel" role="listbox">
            {options.length === 0 ? (
              <p className="no-options-text">No options available</p>
            ) : (
              options.map((o) => (
                <CheckboxRow key={o.filterValue} option={o} checked={o.isSelect} onToggle={onToggleOption} />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Pulls the API `value` (falling back to the label) for every selected option. */
const selectedValuesOf = (options: FilterOption[]) =>
  options.filter((o) => o.isSelect).map((o) => o.value ?? o.filterValue);

const MissedScreenshots = () => {
  const controllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  const [openDropdown, setOpenDropdown] = useState<FilterId | null>(null);

  // All three filter fields' available options live in one object, keyed
  // by FilterId, instead of three separate parallel useState hooks.
  const [filterOptions, setFilterOptions] = useState<FilterState>({
    department: [],
    designation: [],
    systemStatus: STATIC_STATUS_OPTIONS,
  });

  // The applied (server-bound) selection, derived the same way.
  const [appliedFilters, setAppliedFilters] = useState<Record<FilterId, string[]>>({
    department: [],
    designation: [],
    systemStatus: [],
  });

  const [rows, setRows] = useState<MissedScreenshotItem[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);

  // Only used for the very first load (no rows/filters fetched yet). Unlike
  // before, later searches/filters/page-changes do NOT flip this back to
  // true — doing so was unmounting the whole page (search box included) on
  // every debounced search, which made the input lose focus and made it
  // *look* like a search was firing on every keystroke.
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [modalOpenDropdown, setModalOpenDropdown] = useState<FilterId | null>(null);
  const [modalFilters, setModalFilters] = useState<FilterState>({
    department: [],
    designation: [],
    systemStatus: [],
  });

  /** Load departments and designations (cancelled on unmount). */
  useEffect(() => {
    const controller = new AbortController();

    const loadOptions = (endpoint: string, id: "department" | "designation") => {
      commonGetService(endpoint, controller.signal).then((res) => {
        if (res.status === 200) {
          const options: FilterOption[] = (res.data || []).map((item: any) => ({
            filterValue: item.label,
            isSelect: false,
            value: item.value,
          }));
          setFilterOptions((prev) => ({ ...prev, [id]: options }));
        } else if (res.status !== "cancelled") {
          setFilterOptions((prev) => ({ ...prev, [id]: [] }));
        }
      });
    };

    loadOptions(DEPARTMENTS_ENDPOINT, "department");
    loadOptions(DESIGNATIONS_ENDPOINT, "designation");

    return () => controller.abort();
  }, []);

  /** Fetch missed screenshots whenever filters, search, or page change. */
  useEffect(() => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    // NOTE: intentionally no setIsLoading(true) here. isLoading starts
    // true and is set false once, below — exactly like Employee Last
    // Tracked. Re-arming it on every fetch was what unmounted the search
    // input (and the rest of the page) each time the debounced search
    // fired, causing it to lose focus mid-typing.

    const page = Math.max(current - 1, 0);
    const params = new URLSearchParams({ page: String(page), size: String(PAGE_SIZE) });

    if (appliedFilters.department.length) params.set("department_ids", appliedFilters.department.join(","));
    if (appliedFilters.designation.length) params.set("designation_ids", appliedFilters.designation.join(","));
    if (appliedFilters.systemStatus.length) params.set("current_status", appliedFilters.systemStatus.join(","));
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());

    commonGetService(`${SCREENSHOTS_ENDPOINT}?${params.toString()}`, signal).then((res) => {
      if (res.status === 200) {
        setRows(res.data?.content ?? []);
        setTotalElements(res.data?.totalElements ?? 0);
        setIsLoading(false);
      } else if (res.status !== "cancelled") {
        setRows([]);
        setTotalElements(0);
        setIsLoading(false);
      }
    });

    return () => controllerRef.current?.abort();
  }, [appliedFilters, debouncedSearch, current]);

  /** Reset pagination when search changes. */
  useEffect(() => {
    setCurrent(1);
  }, [debouncedSearch]);

  /** Close desktop dropdown when clicking anywhere outside the current filter. */
  useEffect(() => {
    if (!openDropdown) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(".inline-filter")) return;
      setOpenDropdown(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [openDropdown]);

  const handleToggleDropdown = useCallback((id: FilterId) => {
    setOpenDropdown((prev) => (prev === id ? null : id));
  }, []);

  const handleApplyFilter = useCallback((id: FilterId, options: FilterOption[]) => {
    setFilterOptions((prev) => ({ ...prev, [id]: options }));
    setAppliedFilters((prev) => ({ ...prev, [id]: selectedValuesOf(options) }));
    setOpenDropdown(null);
    setCurrent(1);
  }, []);

  const hasActiveFilters = useMemo(
    () => Object.values(appliedFilters).some((values) => values.length > 0),
    [appliedFilters]
  );

  const handleClearAll = useCallback(() => {
    setFilterOptions((prev) => {
      const cleared: FilterState = {} as FilterState;
      (Object.keys(prev) as FilterId[]).forEach((id) => {
        cleared[id] = prev[id].map((o) => ({ ...o, isSelect: false }));
      });
      return cleared;
    });
    setAppliedFilters({ department: [], designation: [], systemStatus: [] });
    setCurrent(1);
  }, []);

  /** Tablet modal handlers. */
  const handleOpenFilterModal = useCallback(() => {
    setModalFilters({
      department: filterOptions.department.map((o) => ({ ...o })),
      designation: filterOptions.designation.map((o) => ({ ...o })),
      systemStatus: filterOptions.systemStatus.map((o) => ({ ...o })),
    });
    setModalOpenDropdown(null);
    setShowFilterModal(true);
  }, [filterOptions]);

  const handleCloseFilterModal = useCallback(() => {
    setShowFilterModal(false);
    setModalOpenDropdown(null);
  }, []);

  const handleModalToggleDropdown = useCallback((id: FilterId) => {
    setModalOpenDropdown((prev) => (prev === id ? null : id));
  }, []);

  /** Close tablet dropdown when clicking outside it. */
  useEffect(() => {
    if (!showFilterModal || !modalOpenDropdown) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest(".modal-filter-select-wrapper")) {
        setModalOpenDropdown(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [showFilterModal, modalOpenDropdown]);

  const handleModalToggleOption = useCallback((id: FilterId, filterValue: string) => {
    setModalFilters((prev) => ({
      ...prev,
      [id]: prev[id].map((o) => (o.filterValue === filterValue ? { ...o, isSelect: !o.isSelect } : o)),
    }));
  }, []);

  const handleModalRemoveChip = useCallback((id: FilterId, filterValue: string) => {
    setModalFilters((prev) => ({
      ...prev,
      [id]: prev[id].map((o) => (o.filterValue === filterValue ? { ...o, isSelect: false } : o)),
    }));
  }, []);

  const handleModalApply = useCallback(() => {
    setFilterOptions(modalFilters);
    setAppliedFilters({
      department: selectedValuesOf(modalFilters.department),
      designation: selectedValuesOf(modalFilters.designation),
      systemStatus: selectedValuesOf(modalFilters.systemStatus),
    });
    setShowFilterModal(false);
    setModalOpenDropdown(null);
    setCurrent(1);
  }, [modalFilters]);

  const handleExport = useCallback(() => {
    exportToCsv(rows, "missed-screenshot-captures.csv");
  }, [rows]);

  const onPageChange: PaginationProps["onChange"] = (page) => setCurrent(page);

  return (
    <div className="screenshots-page-outer">
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center h-100 w-100 crt-loader">
          <img src="/images/loader/content_loader.gif" alt="Loading" />
        </div>
      ) : (
        <div className="sst-container d-flex flex-column screenshots-page-container new-page" ref={containerRef}>
          <div className="d-flex flex-row justify-content-between align-items-center page-heading">
            <div className="d-flex flex-row align-items-center heading-with-back">
              <img src="/images/screenshots/Chevron_left.svg" height={25} width={25} alt="" />
              <h4 className="mb-0">Employees with missed screenshot captures</h4>
            </div>

            <div className="d-flex flex-row align-items-center page-heading-actions">
              <button
                type="button"
                className="tablet-filter-btn"
                aria-label="Open filters"
                onClick={handleOpenFilterModal}
              >
                <Image src="/images/employees/filtericon.svg" alt="" aria-hidden="true" className="tablet-filter-btn-icon" />
                <span className="tablet-filter-btn-text">Filter</span>
              </button>

              <button type="button" className="export-btn" onClick={handleExport} disabled={!rows.length}>
                <Image src="/images/employees/download_icon.svg" alt="" aria-hidden="true" />
                Export
              </button>
            </div>
          </div>

          <div className="sst-filters d-flex flex-column">
            <div className="search-bar">
              <Form.Control
                type="text"
                aria-label="Search employees by name, ID, or department"
                placeholder="Search employees by name, ID, or department"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Image src="/images/dashboard/search.svg" alt="" aria-hidden="true" className="position-absolute" />
            </div>

            <div className="fltr-fld-cont">
              <div className="fltr-fld">
                {FILTER_FIELDS.map((field) => (
                  <InlineFilter
                    key={field.id}
                    id={field.id}
                    label={field.label}
                    placeholder={field.placeholder}
                    options={filterOptions[field.id]}
                    isOpen={openDropdown === field.id}
                    onToggle={handleToggleDropdown}
                    onApply={handleApplyFilter}
                  />
                ))}
              </div>

              {hasActiveFilters && (
                <div className="clr-txt" role="button" tabIndex={0} onClick={handleClearAll}>
                  Clear All
                </div>
              )}
            </div>
          </div>

          {/* Tablet Filter Modal */}
          {showFilterModal && (
            <div
              className="tablet-filter-modal-overlay"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) handleCloseFilterModal();
              }}
            >
              <div
                className="tablet-filter-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="tablet-filter-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="tablet-filter-modal-header">
                  <h5 id="tablet-filter-modal-title">Filter</h5>
                  <button
                    type="button"
                    className="tablet-filter-modal-close"
                    aria-label="Close filter"
                    onClick={handleCloseFilterModal}
                  >
                    ×
                  </button>
                </div>

                <div className="tablet-filter-modal-body">
                  {FILTER_FIELDS.map((field) => (
                    <ModalFilterField
                      key={field.id}
                      label={field.label}
                      placeholder={field.placeholder}
                      options={modalFilters[field.id]}
                      isOpen={modalOpenDropdown === field.id}
                      onToggle={() => handleModalToggleDropdown(field.id)}
                      onToggleOption={(v) => handleModalToggleOption(field.id, v)}
                      onRemoveChip={(v) => handleModalRemoveChip(field.id, v)}
                    />
                  ))}
                </div>

                <div className="tablet-filter-modal-footer">
                  <button type="button" className="modal-cancel-btn" onClick={handleCloseFilterModal}>
                    Cancel
                  </button>
                  <button type="button" className="modal-apply-btn" onClick={handleModalApply}>
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="sst-table-wrapper">
            <table className="sst-table">
              <thead>
                <tr>
                  <th>Emp ID</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Last Screenshot Capture</th>
                  <th>System Status</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((item) => {
                  const statusMeta = getStatusMeta(item.system_status);
                  const agoDisplay = getAgoDisplay(item.last_screenshot_capture_ago);

                  return (
                    <tr key={item.employee_id}>
                      <td className="emp-id">
                        <span className="table-tooltip-text" title={item.employee_id}>
                          {item.employee_id}
                        </span>
                      </td>

                      <td className="emp-cell">
                        <Image src={item.employee_image} alt="" aria-hidden="true" roundedCircle className="emp-avatar" />
                        <div className="emp-cell-text">
                          <div className="emp-name" title={item.employee_name}>
                            {item.employee_name}
                          </div>
                          <div className="emp-email table-tooltip-text" title={item.employee_mail}>
                            {item.employee_mail}
                          </div>
                        </div>
                      </td>

                      <td>
                        <span className="table-tooltip-text" title={item.department}>
                          {item.department}
                        </span>
                      </td>

                      <td className="designation-cell">
                        <span className="table-tooltip-text" title={item.designation}>
                          {item.designation}
                        </span>
                      </td>

                      <td className="capture-cell">
                        <div className="capture-time table-tooltip-text" title={item.last_screenshot_capture || "-"}>
                          {item.last_screenshot_capture || "-"}
                          <div>{item.last_screenshot_capture_ago || "-"}</div>
                        </div>
                        {agoDisplay && <div className="capture-ago">{agoDisplay}</div>}
                      </td>

                      <td>
                        <span className="status-pill">
                          <span className={statusMeta.className} />
                          <span className="table-tooltip-text" title={statusMeta.label}>
                            {statusMeta.label}
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!isLoading && rows.length === 0 && (
              <div className="d-flex flex-row w-100 no-data-row no-screenshots">
                <div className="no-data-image-wrapper">
                  <Image src="/images/dashboard/folder_icon.svg" alt="" aria-hidden="true" />
                </div>
                <p>No Screenshots to show</p>
              </div>
            )}
          </div>

          {totalElements > 0 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination
                className="employee-pagination"
                current={current}
                onChange={onPageChange}
                total={totalElements}
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MissedScreenshots;